import { useListState } from "../../lib/listState";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ClipboardCheck, FolderKanban, MapPin, Palette } from "lucide-react";
import { api } from "../../lib/api";
import type { TechOpsProjectRow } from "../../lib/types";
import { formatDateTime, formatNumber, labelFromCode, statusTone } from "../shared/format";
import { RouteError, RouteLoading } from "../shared/PageState";

const PROJECT_STATUSES = ["AWAITING_ARTWORKS", "READY", "IN_PROGRESS", "COMPLETED"];

function StatTile({ label, value, detail }: { label: string; value: number; detail: string }) {
  return (
    <div className="pa-stat-tile">
      <small>{label}</small>
      <strong>{formatNumber(value)}</strong>
      <span>{detail}</span>
    </div>
  );
}

function ratioText(done: number, total: number) {
  if (total === 0) {
    return "0 / 0";
  }
  return `${formatNumber(done)} / ${formatNumber(total)}`;
}

function ProjectRows({ projects }: { projects: TechOpsProjectRow[] }) {
  if (projects.length === 0) {
    return (
      <tr>
        <td colSpan={8} className="pa-empty-cell">No projects match the current filters.</td>
      </tr>
    );
  }

  return projects.map((project) => (
    <tr key={project.project_id}>
      <td>
        <strong>{project.project_name}</strong>
        <small>{project.project_id}</small>
      </td>
      <td>
        <strong>{project.owner_organization_name}</strong>
        <small>{project.owner_organization_type}</small>
      </td>
      <td>
        <strong>{project.agency_organization_name}</strong>
        <small>Agency</small>
      </td>
      <td>
        <strong>{project.vendor_organization_name}</strong>
        <small>Vendor</small>
      </td>
      <td><span className={`pa-badge ${statusTone(project.status)}`}>{labelFromCode(project.status)}</span></td>
      <td><span className={`pa-badge ${statusTone(project.risk_level)}`}>{project.risk_level}</span></td>
      <td>
        <strong>{ratioText(project.completed_location_count, project.total_location_count)}</strong>
        <small>{ratioText(project.uploaded_artwork_count, project.required_artwork_count)} artworks</small>
      </td>
      <td>
        <strong>{formatNumber(project.pending_task_count)}</strong>
        <small>{formatNumber(project.pending_approval_count)} approvals</small>
      </td>
    </tr>
  ));
}

export default function ProjectsPage() {
  const [ownerOrganizationId, setOwnerOrganizationId] = useListState("projects.ownerOrganizationId", "ALL");
  const [status, setStatus] = useListState("projects.status", "ALL");
  const projectsQuery = useQuery({
    queryKey: ["tech-ops-projects", ownerOrganizationId, status],
    queryFn: () => api.listProjects({ owner_organization_id: ownerOrganizationId, status })
  });

  if (projectsQuery.isLoading) {
    return <RouteLoading />;
  }

  if (projectsQuery.isError || !projectsQuery.data) {
    return (
      <RouteError
        title="Projects unavailable"
        detail="The platform project list could not be loaded from the Coprint backend."
        onRetry={() => projectsQuery.refetch()}
      />
    );
  }

  const {
    summary,
    owner_organizations: ownerOrganizations,
    projects,
    generated_at: generatedAt
  } = projectsQuery.data;

  return (
    <div className="pa-page-stack">
      <div className="pa-title-row">
        <div>
          <p className="pa-eyebrow">Projects</p>
          <h1>Project operations</h1>
          <p>Project ownership, lifecycle status, delivery progress and approval load.</p>
        </div>
        <span className="pa-refresh-chip">Updated {formatDateTime(generatedAt)}</span>
      </div>

      <section className="pa-stat-grid five">
        <StatTile label="Total" value={summary.total_projects} detail="Projects" />
        <StatTile label="Active" value={summary.active_projects} detail="Ready or in progress" />
        <StatTile label="Completed" value={summary.completed_projects} detail="Closed projects" />
        <StatTile label="High Risk" value={summary.high_risk_projects} detail="Needs attention" />
        <StatTile label="Approvals" value={summary.pending_approval_projects} detail="Projects waiting" />
      </section>

      <section className="pa-panel">
        <div className="pa-toolbar">
          <select value={ownerOrganizationId} onChange={(event) => setOwnerOrganizationId(event.target.value)}>
            <option value="ALL">All owner organizations</option>
            {ownerOrganizations.map((organization) => (
              <option key={organization.organization_id} value={organization.organization_id}>
                {organization.organization_name} - {labelFromCode(organization.organization_type)}
              </option>
            ))}
          </select>
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="ALL">All project statuses</option>
            {PROJECT_STATUSES.map((projectStatus) => (
              <option key={projectStatus} value={projectStatus}>{labelFromCode(projectStatus)}</option>
            ))}
          </select>
        </div>
        <div className="pa-strip-metrics">
          <span><FolderKanban size={15} /> {formatNumber(projects.length)} listed projects</span>
          <span><MapPin size={15} /> {formatNumber(projects.reduce((sum, project) => sum + project.total_location_count, 0))} listed locations</span>
          <span><Palette size={15} /> {formatNumber(projects.reduce((sum, project) => sum + project.required_artwork_count, 0))} expected artworks</span>
          <span><ClipboardCheck size={15} /> {formatNumber(projects.reduce((sum, project) => sum + project.pending_approval_count, 0))} pending approvals</span>
          <span><AlertTriangle size={15} /> {formatNumber(projects.filter((project) => project.risk_level === "HIGH").length)} high risk</span>
        </div>
        <div className="pa-table-scroll">
          <table className="pa-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Owner Organization</th>
                <th>Agency</th>
                <th>Vendor</th>
                <th>Status</th>
                <th>Risk</th>
                <th>Progress</th>
                <th>Open Work</th>
              </tr>
            </thead>
            <tbody>
              <ProjectRows projects={projects} />
            </tbody>
          </table>
        </div>
      </section>

      <div className="pa-footnote-strip">
        <span><CheckCircle2 size={15} /> Organization filter uses project owner organization.</span>
        <span><FolderKanban size={15} /> Results are capped to the 100 highest-attention projects.</span>
      </div>
    </div>
  );
}
